import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { formatCurrency, formatDate, statusSpacerMeta } from '../utils';
import Modal from '../components/Modal';
import { Input, Btn } from '../components/Input';
import {
  IconEdit, IconTrash, IconLink, IconPlus, IconCheck, IconSnowflake,
  IconBuilding, IconDoc, IconAlert, IconChevronRight, IconX,
} from '../components/Icons';
import { SpacerForm } from './Spacery';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import HelpHint from '../components/HelpHint';
import { Skeleton, SkeletonCard } from '../components/Skeleton';

// ─── Modal: Oznacz jako zapłaconą ─────────────────────────────────────────────

function ZaplaconaModal({ spacerId, platnosc, onClose, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.updatePlatnoscStatus(spacerId, platnosc.id, 'zaplacona', date);
      toast.success(t('toast.updated'));
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      <div style={{ background: 'var(--bg2)', borderRadius: '8px', padding: '10px 12px' }} className="space-y-0.5">
        {platnosc.nr_faktury && (
          <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('spaceDetail.invoiceLabel')}: <span style={{ color: 'var(--ink)' }}>{platnosc.nr_faktury}</span></p>
        )}
        <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('common.amount')}: <span style={{ color: 'var(--ink)', fontFamily: 'monospace' }}>{formatCurrency(platnosc.kwota)}</span></p>
        <p style={{ fontSize: '11px', color: 'var(--ink2)' }}>{t('spaceDetail.period')}: <span style={{ color: 'var(--ink)' }}>{formatDate(platnosc.data_od)} – {formatDate(platnosc.data_do)}</span></p>
      </div>
      <Input label={t('spaceDetail.paymentReceived')} type="date" value={date} onChange={e => setDate(e.target.value)} />
      <div className="flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
        <Btn variant="success" onClick={submit} disabled={loading}>
          <span className="flex items-center gap-1.5"><IconCheck className="w-3.5 h-3.5" /> {t('spaceDetail.markPaid')}</span>
        </Btn>
      </div>
    </div>
  );
}

// ─── Modal: Dodaj fakturę ─────────────────────────────────────────────────────

function DodajFaktureModal({ spacer, kwotaInit, dataDoInit, onClose, onDone }) {
  const { t } = useTranslation();
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const defaultTermin = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const [nrFaktury, setNrFaktury]       = useState('');
  const [kwota, setKwota]               = useState(kwotaInit != null ? String(Math.round(kwotaInit * 100) / 100) : '');
  const [dataWystawienia, setDataW]     = useState(today);
  const [termin, setTermin]             = useState(defaultTermin);
  const [forma, setForma]               = useState('przelew');
  const [status, setStatus]             = useState('wystawiona');
  const [dataWplaty, setDataWplaty]     = useState(today);
  const [pokrycie, setPokrycie]         = useState(null); // { data_od, data_do, miesiace, reszta }
  const [loading, setLoading]           = useState(false);
  // Dopóki użytkownik nie zmienił kwoty, dataDoInit z estymacji "do daty" pozostaje wiążąca —
  // dzięki temu nie tracimy dnia miesiąca przy round-tripie kwota → data.
  const userEditedKwota = useRef(false);

  useEffect(() => {
    if (!kwota || Number(kwota) <= 0) { setPokrycie(null); return; }
    let cancelled = false;
    const useInitDate = dataDoInit && !userEditedKwota.current;
    const body = useInitDate ? { data_do: dataDoInit } : { kwota: Number(kwota) };
    // Debounce 300ms — bez tego każdy keystroke kwoty robi request.
    const timer = setTimeout(() => {
      api.podgladPlatnosci(spacer.id, body)
        .then(p => { if (!cancelled) setPokrycie(p); })
        .catch(() => { if (!cancelled) setPokrycie(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [kwota, spacer.id, dataDoInit]);

  const submit = async () => {
    if (!kwota || Number(kwota) <= 0) { toast.error(t('validation.invoiceAmountRequired')); return; }
    if (!pokrycie) { toast.error(t('validation.waitForCoverage')); return; }
    setLoading(true);
    try {
      await api.addPlatnosc(spacer.id, {
        kwota: Number(kwota),
        data_do: pokrycie.data_do,
        nr_faktury: nrFaktury || null,
        data_wystawienia: dataWystawienia,
        termin_platnosci: termin,
        forma_platnosci: forma,
        status_poczatkowy: status,
        data_wplaty: status === 'zaplacona' ? dataWplaty : null,
        notatki: null,
      });
      toast.success(t('toast.added'));
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Kwota + pokrycie */}
      <div className="flex flex-col gap-1">
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('spaceDetail.invoiceAmount')}</label>
        <input type="number" min="0" step="0.01" placeholder="np. 180"
          className="ds-input"
          value={kwota} onChange={e => { userEditedKwota.current = true; setKwota(e.target.value); }} autoFocus />
        {pokrycie && (
          <div style={{ background: 'rgba(184,84,40,0.08)', border: '1px solid rgba(184,84,40,0.2)', borderRadius: '8px', padding: '8px 12px', fontSize: '10px', color: 'var(--acc2)' }} className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 items-center">
            <span>{t('spaceDetail.coverageMonths', { months: pokrycie.miesiace })}</span>
            <span>{t('spaceDetail.coverageFrom')}: <span style={{ color: 'var(--ink)' }}>{formatDate(pokrycie.data_od)}</span></span>
            <span>{t('spaceDetail.coveredUntil')}: <span style={{ color: 'var(--ok2)', fontWeight: 600 }}>{formatDate(pokrycie.data_do)}</span></span>
            {pokrycie.reszta > 0 && (
              <span style={{ color: 'var(--acc2)' }}>{t('spaceDetail.remainder', { amount: formatCurrency(pokrycie.reszta) })}</span>
            )}
          </div>
        )}
      </div>

      <Input label={t('spaceDetail.invoiceNumber')} placeholder="np. FV/2026/001"
        value={nrFaktury} onChange={e => setNrFaktury(e.target.value)} />

      <div className="grid sm:grid-cols-2 gap-3">
        <Input label={t('spaceDetail.issueDate')} type="date"
          value={dataWystawienia} onChange={e => setDataW(e.target.value)} />
        <Input label={t('spaceDetail.dueDate')} type="date"
          value={termin} onChange={e => setTermin(e.target.value)} />
      </div>

      {/* Forma płatności */}
      <div className="flex flex-col gap-1">
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('spaceDetail.paymentMethod')}</label>
        <div className="flex gap-2">
          {[{ id: 'przelew', label: t('spaceDetail.transfer') }, { id: 'gotowka', label: t('spaceDetail.cash') }].map(f => (
            <button key={f.id} type="button" onClick={() => setForma(f.id)}
              className="flex-1 py-2 rounded-btn transition-colors"
              style={forma === f.id
                ? { background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700 }
                : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status */}
      <div className="flex flex-col gap-1">
        <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('spaceDetail.status')}</label>
        <div className="flex gap-2">
          {[{ id: 'wystawiona', label: t('spaceDetail.issued') }, { id: 'zaplacona', label: t('spaceDetail.paid') }].map(s => (
            <button key={s.id} type="button" onClick={() => setStatus(s.id)}
              className="flex-1 py-2 rounded-btn transition-colors"
              style={status === s.id
                ? s.id === 'zaplacona'
                  ? { background: 'rgba(90,158,114,0.18)', color: 'var(--ok2)', border: '1px solid rgba(90,158,114,0.35)', fontSize: '9px', fontWeight: 700 }
                  : { background: 'rgba(184,84,40,0.15)', color: 'var(--acc2)', border: '1px solid rgba(184,84,40,0.25)', fontSize: '9px', fontWeight: 700 }
                : { background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '9px', fontWeight: 700 }}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {status === 'zaplacona' && (
        <Input label={t('spaceDetail.paymentReceivedDate')} type="date" value={dataWplaty} onChange={e => setDataWplaty(e.target.value)} />
      )}

      <div className="flex justify-end gap-2 pt-1">
        <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
        <Btn onClick={submit} disabled={loading || !pokrycie}>
          {loading ? t('common.saving') : `${t('spaceDetail.addInvoice')}${pokrycie ? ` ${t('spaceDetail.addedUntil', { date: formatDate(pokrycie.data_do) })}` : ''}`}
        </Btn>
      </div>
    </div>
  );
}

// ─── Karta: Estymacja rozliczeń ───────────────────────────────────────────────

function KartaEstymacja({ spacer, onDodajFakture }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('dzis');
  const [kwota, setKwota] = useState('');
  const [dataDo, setDataDo] = useState('');
  const [podglad, setPodglad] = useState(null);

  const today = new Date().toISOString().slice(0, 10);
  const wDarmowym = today <= spacer.koniec_darmowego;

  const fetchPodglad = useCallback(async () => {
    if (mode === 'kwota' && !kwota) { setPodglad(null); return; }
    if (mode === 'data' && !dataDo)  { setPodglad(null); return; }
    try {
      let body = {};
      if (mode === 'dzis')  body = { data_do: today };
      else if (mode === 'data') body = { data_do: dataDo };
      else                  body = { kwota: Number(kwota) };
      const p = await api.podgladPlatnosci(spacer.id, body);
      setPodglad(p);
    } catch { setPodglad(null); }
  }, [mode, kwota, dataDo, spacer.id, today]);

  useEffect(() => { fetchPodglad(); }, [fetchPodglad]);

  return (
    <div className="ds-card p-5 flex flex-col gap-4">
      <div>
        <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {t('spaceDetail.estimation')}
          <HelpHint text={t('help.settlementSim')} label={t('spaceDetail.estimation')} />
        </h2>
        <p style={{ fontSize: '10px', color: 'var(--ink3)' }} className="mt-0.5">{t('spaceDetail.estimationSub')}</p>
      </div>

      {/* Status hosting */}
      <div style={{ background: 'var(--bg2)', borderRadius: '8px', padding: '10px 12px' }} className="space-y-1">
        <div className="flex justify-between">
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.rate')}</span>
          <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{formatCurrency(spacer.stawka_hostingu)}/{t('common.month')}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.freeUntil')}</span>
          <span style={{ fontSize: '11px', color: wDarmowym ? 'var(--ok2)' : 'var(--ink3)' }}>{formatDate(spacer.koniec_darmowego)}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.paidUntil')}</span>
          {spacer.rozliczony_do
            ? <span style={{ fontSize: '11px', color: 'var(--ok2)' }}>{formatDate(spacer.rozliczony_do)}</span>
            : <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('spaceDetail.noPaid')}</span>}
        </div>
        <div className="flex justify-between pt-1" style={{ borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.accrued')}</span>
          {spacer.kwota_narosla > 0
            ? <span style={{ color: 'var(--acc2)', fontFamily: 'monospace', fontWeight: 700 }}>{formatCurrency(spacer.kwota_narosla)}</span>
            : <span style={{ color: 'var(--ok2)' }}>{t('spaceDetail.noDebts')}</span>}
        </div>
      </div>

      {/* Tryby */}
      <div className="flex gap-1">
        {[
          { id: 'dzis',  label: t('spaceDetail.toDay') },
          { id: 'data',  label: t('spaceDetail.toDate') },
          { id: 'kwota', label: t('spaceDetail.byAmount') },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            className="flex-1 py-1.5 rounded-btn transition-colors"
            style={mode === m.id
              ? { background: 'var(--acc)', color: '#fff', fontSize: '9px', fontWeight: 700 }
              : { background: 'var(--bg4)', color: 'var(--ink2)', fontSize: '9px', fontWeight: 700 }}>
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'data' && (
        <Input label={t('spaceDetail.paidToDate')} type="date" value={dataDo} onChange={e => setDataDo(e.target.value)} />
      )}
      {mode === 'kwota' && (
        <div className="flex flex-col gap-1">
          <label style={{ fontSize: '9px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{t('common.amount')}</label>
          <input type="number" min="0" step="0.01" placeholder="np. 180"
            className="ds-input"
            value={kwota} onChange={e => setKwota(e.target.value)} />
        </div>
      )}

      {podglad && podglad.miesiace > 0 && (
        <div className="ds-card-accent p-4 flex flex-col gap-1">
          <div className="flex justify-between">
            <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.period')}</span>
            <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{formatDate(podglad.data_od)} → {formatDate(podglad.data_do)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.months')}</span>
            <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{podglad.miesiace}</span>
          </div>
          <div className="flex justify-between pt-1" style={{ borderTop: '1px solid rgba(184,84,40,0.3)' }}>
            <span style={{ fontSize: '10px', color: 'var(--ink2)', fontWeight: 600 }}>{t('common.amount')}</span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--ink)' }}>{formatCurrency(podglad.kwota)}</span>
          </div>
          {podglad.reszta > 0 && (
            <p style={{ fontSize: '10px', color: 'var(--acc2)' }}>{t('spaceDetail.remainderNoFullMonth', { amount: formatCurrency(podglad.reszta) })}</p>
          )}
          <button onClick={() => onDodajFakture({ kwota: Math.round(podglad.kwota * 100) / 100, dataDo: podglad.data_do })}
            className="mt-2 w-full py-2 rounded-btn flex items-center justify-center gap-1.5 transition-colors"
            style={{ background: 'var(--acc)', color: '#fff', fontSize: '9px', fontWeight: 700 }}>
            <IconDoc className="w-4 h-4" /> {t('spaceDetail.addInvoiceWithAmount')}
          </button>
        </div>
      )}

      {mode === 'dzis' && (!podglad || podglad.miesiace === 0) && (
        <p style={{ fontSize: '10px', color: 'var(--ink3)' }} className="text-center py-2">
          {wDarmowym ? t('spaceDetail.freeperiodActive') : t('spaceDetail.noBacklog')}
        </p>
      )}
    </div>
  );
}

// ─── Karta: Wzmianka o fakturach (pełne zarządzanie u klienta) ────────────────

function KartaFakturaWzmianka({ spacer, platnosci, onDodajFakture, onZaplacona, onNavigate }) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);

  const niezaplacone = platnosci.filter(p => p.status !== 'zaplacona');
  const sumaNiezaplaconych = niezaplacone.reduce((s, p) => s + (Number(p.kwota) || 0), 0);
  const ostatnie = [...platnosci].sort((a, b) =>
    (b.data_wystawienia || '').localeCompare(a.data_wystawienia || '')
  ).slice(0, 3);

  return (
    <div className="ds-card p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          {t('spaceDetail.invoices')}
          <HelpHint text={t('help.hostingInvoices')} label={t('spaceDetail.invoices')} />
        </h2>
        <button onClick={() => onDodajFakture({})}
          className="inline-flex items-center gap-1 rounded-btn"
          style={{ background: 'var(--acc)', color: '#fff', border: '1px solid rgba(184,84,40,0.6)', fontSize: '9px', fontWeight: 700, padding: '5px 12px' }}>
          <IconPlus className="w-3.5 h-3.5" /> {t('spaceDetail.addInvoice')}
        </button>
      </div>

      {/* Podsumowanie */}
      <div style={{ background: 'var(--bg2)', borderRadius: '8px', padding: '10px 12px' }} className="space-y-1">
        <div className="flex justify-between">
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.issuedInvoices')}</span>
          <span style={{ fontSize: '11px', color: 'var(--ink)' }}>{platnosci.length}</span>
        </div>
        <div className="flex justify-between">
          <span style={{ fontSize: '10px', color: 'var(--ink2)' }}>{t('spaceDetail.unpaid')}</span>
          {niezaplacone.length > 0
            ? <span style={{ color: 'var(--acc2)', fontFamily: 'monospace', fontWeight: 700 }}>{niezaplacone.length} · {formatCurrency(sumaNiezaplaconych)}</span>
            : <span style={{ color: 'var(--ok2)' }}>{t('common.none')}</span>}
        </div>
      </div>

      {/* Ostatnie 3 faktury — kompaktowo, read-only */}
      {ostatnie.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {ostatnie.map(p => {
            const zaplacona = p.status === 'zaplacona';
            const przeterminowana = !zaplacona && p.termin_platnosci && p.termin_platnosci < today;
            return (
              <div key={p.id}
                style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px' }}
                className="flex items-center gap-2">
                <IconDoc className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--ink3)' }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--ink)' }}>
                      {p.nr_faktury || <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>{t('spaceDetail.noNumber')}</span>}
                    </span>
                    {przeterminowana && (
                      <span className="pill pill-err" style={{ fontSize: '9px' }}>
                        <IconAlert className="w-2.5 h-2.5" /> {t('spaceDetail.overdue')}
                      </span>
                    )}
                    {zaplacona && (
                      <span className="pill pill-ok" style={{ fontSize: '9px' }}>
                        <IconCheck className="w-2.5 h-2.5" /> {t('spaceDetail.invoiceZaplacona')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--ink3)' }} className="mt-0.5">
                    {formatDate(p.data_od)} → {formatDate(p.data_do)}
                  </div>
                </div>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '11px', color: zaplacona ? 'var(--ok2)' : 'var(--acc2)' }}>
                  {formatCurrency(p.kwota)}
                </span>
                {!zaplacona && (
                  <button onClick={() => onZaplacona(p)} title={t('spaceDetail.invoiceMarkPaid')}
                    className="w-6 h-6 flex items-center justify-center rounded-btn"
                    style={{ color: 'var(--ok2)', border: '1px solid rgba(90,158,114,0.3)' }}>
                    <IconCheck className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {platnosci.length === 0 && (
        <div className="text-center py-4" style={{ color: 'var(--ink3)' }}>
          <IconDoc className="w-7 h-7 mx-auto mb-1.5 opacity-30" />
          <p style={{ fontSize: '11px' }}>{t('spaceDetail.noInvoices')}</p>
        </div>
      )}

      {/* Link do panelu klienta */}
      <button onClick={() => onNavigate('klient', spacer.klient_id)}
        className="flex items-center justify-center gap-1 py-2 rounded-btn transition-colors hover:bg-navy-800"
        style={{ background: 'var(--bg4)', color: 'var(--ink2)', border: '1px solid var(--border)', fontSize: '10px', fontWeight: 700, letterSpacing: '0.04em' }}>
        {t('spaceDetail.manageInvoices')}
        <IconChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Karta: Ustawienia Spaceru 3D (inline) ────────────────────────────────────

function KartaUstawienia({ spacer, klienci, onSave, open, onToggle }) {
  const { t } = useTranslation();
  return (
    <div className="ds-card overflow-hidden mt-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:bg-navy-800">
        <div className="flex items-center gap-2">
          <IconEdit className="w-4 h-4" style={{ color: 'var(--ink3)' }} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)' }}>{t('spaceDetail.settings3D')}</span>
        </div>
        <IconChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} style={{ color: 'var(--ink3)' }} />
      </button>
      {open && (
        <div className="px-5 pb-5" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="pt-4">
            <SpacerForm
              klienci={klienci}
              initial={{ ...spacer, przelane_wykonanie: !!spacer.przelane_wykonanie }}
              onSave={onSave}
              onClose={onToggle}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Główny komponent SpacerDetail ────────────────────────────────────────────

function SpacerPodglad({ miniatura, link, nazwa }) {
  const { t } = useTranslation();
  const [iframe, setIframe] = useState(false);

  return (
    <div className="ds-card overflow-hidden mb-2">
      {iframe && link ? (
        <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
          <iframe
            src={link}
            title={nazwa}
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          />
          <button
            onClick={() => setIframe(false)}
            style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', zIndex: 2, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <IconX className="w-3 h-3" /> {t('common.close')}
          </button>
        </div>
      ) : (
        <div style={{ position: 'relative', background: 'var(--bg3)' }}>
          {miniatura ? (
            <img src={miniatura} alt={nazwa} style={{ width: '100%', maxHeight: '260px', objectFit: 'cover', display: 'block' }} />
          ) : (
            <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconBuilding style={{ width: '48px', height: '48px', color: 'var(--ink3)' }} />
            </div>
          )}
          {link && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <button onClick={() => setIframe(true)} style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', border: '2px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: '50px', padding: '10px 22px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>▶</span> {t('spaceDetail.launchPreview')}
              </button>
            </div>
          )}
        </div>
      )}
      {link && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('spaceDetail.matterportLink')}</span>
          <a href={link} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '11px', fontWeight: 600, color: 'var(--acc)', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}>
            <IconLink style={{ width: '13px', height: '13px' }} /> {t('spaceDetail.openInBrowser')}
          </a>
        </div>
      )}
    </div>
  );
}

export default function SpacerDetail({ spacerId, onNavigate }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const [spacer, setSpacer]     = useState(null);
  const [platnosci, setPlatnosci] = useState([]);
  const [klienci, setKlienci]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Modals
  const [fakturaModal, setFakturaModal]   = useState(null); // null | { kwotaInit? }
  const [zaplaconaModal, setZaplaconaModal] = useState(null); // platnosc | null

  const load = useCallback(async () => {
    if (!spacerId) return;
    setLoading(true);
    try {
      const [s, p, k] = await Promise.all([
        api.getSpacer(spacerId),
        api.getPlatnosci(spacerId),
        api.getKlienci(),
      ]);
      setSpacer(s); setPlatnosci(p); setKlienci(k);
    } catch (e) { toast.error(t('common.loadError', { message: e.message })); }
    finally { setLoading(false); }
  }, [spacerId, t, toast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('confirmDialog.deleteTitle'),
      message: t('spaces.deleteConfirm', { name: spacer.nazwa }),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteSpacer(spacer.id);
      toast.success(t('toast.deleted'));
      onNavigate('spacery');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleEdit = async form => {
    await api.updateSpacer(spacer.id, form);
    setSettingsOpen(false);
    await load();
  };

  if (loading) return (
    <div className="flex flex-col gap-4">
      <div className="ds-card" style={{ padding: '20px' }}>
        <Skeleton w="55%" h="18px" />
        <div style={{ marginTop: '8px' }}><Skeleton w="30%" h="10px" /></div>
      </div>
      <SkeletonCard lines={3} />
      <SkeletonCard lines={2} />
    </div>
  );

  if (!spacer) return (
    <div className="flex items-center justify-center py-20">
      <p style={{ color: 'var(--ink3)' }}>{t('spaceDetail.notFound')}</p>
    </div>
  );

  const meta = statusSpacerMeta(spacer.status);

  const statusPillClass = spacer.status === 'aktywny' ? 'pill pill-ok'
    : spacer.status === 'zamrozony' ? 'pill pill-blue'
    : spacer.status === 'usuniety' ? 'pill pill-err'
    : 'pill pill-gray';

  return (
    <div className="max-w-5xl w-full mx-auto">

      {/* Nawigacja */}
      <div className="flex items-center gap-2 mb-4" style={{ fontSize: '11px' }}>
        <button onClick={() => onNavigate('spacery')} style={{ color: 'var(--ink2)' }} className="hover:underline transition-colors">
          ← {t('spaces.title')}
        </button>
        <span style={{ color: 'var(--ink3)' }}>/</span>
        <button onClick={() => onNavigate('klient', spacer.klient_id)}
          style={{ color: 'var(--ink2)' }} className="hover:underline transition-colors">
          {spacer.klient_nazwa}
        </button>
      </div>

      {/* Header */}
      <div className="ds-card p-4 sm:p-5 mb-2">
        <div className="flex gap-3 sm:gap-4 items-start flex-wrap">
          {spacer.miniatura ? (
            <img src={spacer.miniatura} alt={spacer.nazwa}
              className="w-[88px] h-[58px] sm:w-[120px] sm:h-[80px] object-cover rounded-lg flex-shrink-0" style={{ border: '1px solid var(--border)' }} />
          ) : (
            <div className="w-[88px] h-[58px] sm:w-[120px] sm:h-[80px] rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg2)', borderRadius: '8px' }}>
              <IconBuilding className="w-8 h-8" style={{ color: 'var(--ink3)' }} />
            </div>
          )}

          <div className="flex-1 min-w-0" style={{ flex: '1 1 200px' }}>
            <h1 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--ink)' }} className="truncate">{spacer.nazwa}</h1>
            {spacer.adres && <p style={{ fontSize: '11px', color: 'var(--ink2)' }} className="mt-0.5 truncate">{spacer.adres}</p>}
            <button onClick={() => onNavigate('klient', spacer.klient_id)}
              style={{ fontSize: '11px', color: 'var(--ink2)' }} className="mt-1 block hover:underline">
              {spacer.klient_nazwa}
            </button>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`${statusPillClass} inline-flex items-center gap-1`}>
                {spacer.status === 'zamrozony' && <IconSnowflake className="w-3 h-3" />}
                {meta.label}
              </span>
              {spacer.created_at && (
                <span style={{ fontSize: '10px', color: 'var(--ink3)' }}>
                  {t('spaceDetail.createdAt')}: {new Date(spacer.created_at).toLocaleDateString()}
                </span>
              )}
              {spacer.status === 'usuniety' && spacer.data_usuniecia && (
                <span style={{ fontSize: '10px', color: 'var(--err2)' }}>
                  {t('spaces.deletedOn')}: {formatDate(spacer.data_usuniecia)}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
            {spacer.link_matterport && (
              <a href={spacer.link_matterport} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-btn transition-colors hover:bg-navy-800"
                style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink2)', border: '1px solid var(--border)' }}>
                <IconLink className="w-3.5 h-3.5" /> {t('spaceDetail.linkShort')}
              </a>
            )}
            <button onClick={handleDelete}
              className="w-7 h-7 flex items-center justify-center rounded-btn transition-colors hover:bg-navy-800"
              style={{ color: 'var(--err2)' }} title={t('common.delete')}>
              <IconTrash className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Podgląd spaceru */}
      {(spacer.miniatura || spacer.link_matterport) && (
        <SpacerPodglad miniatura={spacer.miniatura} link={spacer.link_matterport} nazwa={spacer.nazwa} />
      )}

      {/* Pasek wykonania */}
      {spacer.kwota_wykonania != null && (
        <div className="ds-card px-5 py-3 mb-6 flex flex-wrap gap-x-5 gap-y-1 items-center">
          <span style={{ fontSize: '11px', color: 'var(--ink2)' }}>
            {t('spaceDetail.execution')}: <span style={{ fontWeight: 700, color: 'var(--ink)', fontFamily: 'monospace' }}>{formatCurrency(spacer.kwota_wykonania)}</span>
          </span>
          {spacer.nr_faktury && (
            <span style={{ fontSize: '11px', color: 'var(--ink2)' }}>FV: <span style={{ color: 'var(--ink)' }}>{spacer.nr_faktury}</span></span>
          )}
          <span className="flex items-center gap-1" style={{ fontSize: '11px', color: spacer.przelane_wykonanie ? 'var(--ok2)' : 'var(--acc2)' }}>
            {spacer.przelane_wykonanie ? <><IconCheck className="w-3.5 h-3.5" /> {t('spaceDetail.transferred')}</> : t('spaceDetail.notTransferred')}
          </span>
        </div>
      )}

      {/* Siatka: Estymacja + Faktury */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
        <KartaEstymacja
          spacer={spacer}
          onDodajFakture={({ kwota, dataDo } = {}) => setFakturaModal({ kwotaInit: kwota, dataDoInit: dataDo })}
        />
        <KartaFakturaWzmianka
          spacer={spacer}
          platnosci={platnosci}
          onDodajFakture={() => setFakturaModal({})}
          onZaplacona={p => setZaplaconaModal(p)}
          onNavigate={onNavigate}
        />
      </div>

      {/* Ustawienia Spaceru 3D — inline */}
      <KartaUstawienia
        spacer={spacer}
        klienci={klienci}
        onSave={handleEdit}
        open={settingsOpen}
        onToggle={() => setSettingsOpen(o => !o)}
      />

      {/* Modal: Dodaj fakturę */}
      {fakturaModal && (
        <Modal title={t('spaceDetail.addInvoice')} onClose={() => setFakturaModal(null)}>
          <DodajFaktureModal
            spacer={spacer}
            kwotaInit={fakturaModal.kwotaInit}
            dataDoInit={fakturaModal.dataDoInit}
            onClose={() => setFakturaModal(null)}
            onDone={load}
          />
        </Modal>
      )}

      {/* Modal: Oznacz jako zapłaconą */}
      {zaplaconaModal && (
        <Modal title={t('spaceDetail.markAsInvoicePaid')} onClose={() => setZaplaconaModal(null)}>
          <ZaplaconaModal
            spacerId={spacer.id}
            platnosc={zaplaconaModal}
            onClose={() => setZaplaconaModal(null)}
            onDone={load}
          />
        </Modal>
      )}

    </div>
  );
}
